import { IDocumentViewModel } from '../../../../services/Document/Api';
import { ExecutionContext } from '../../../../services/backendServices/ViewModels';
import { AxiosInstance } from 'axios';
import { RuntimeRuleNonLibrary } from '../../CoreEngine/Utility/RuntimeRule';
import { UIDocumentController } from '../../ScriptingEngineEnvironment/Touchpoint';
import { ContextInformationCollector } from './ContextInformationCollector';
import { DocumentFieldController } from '../../../DocumentEditRules/ValidationService/DocumentFieldController';
import { IFieldControlStates } from '../../../DocumentEditRules/types/public';
import { PODocumentsForInvoice } from './extractPONumbers';

type RuleRegistration = {
    scriptName: string;
    dpsRuleName: RuntimeRuleNonLibrary['dpsRuleName'];
    configParams: RuntimeRuleNonLibrary['configParams'];
    ordinal: RuntimeRuleNonLibrary['ordinal'];
    documentControllers: UIDocumentController.ControllerFn[];
};
export class UIDocumentControllerTouchPoint implements UIDocumentController.IUIDocumentControllerTouchPoint {
    private registeredRules = new Map<string, RuleRegistration>();

    private currentScript: string | null = null;

    private hasCompletedRegistration = false;

    constructor(
        private readonly context: ExecutionContext,
        private api: () => AxiosInstance,
        private buyerCompanyId?: number,
        private senderCompanyId?: number,
        private parentCompanyId?: number, // for multi-company scenarios
        private writeToLog?: (message: string) => void
    ) {}

    setCurrentScript(rule: RuntimeRuleNonLibrary) {
        if (this.hasCompletedRegistration) {
            throw new Error('Registration has already been completed.');
        }

        this.currentScript = rule.name;

        this.registeredRules.set(rule.name, {
            scriptName: rule.name,
            dpsRuleName: rule.dpsRuleName,
            configParams: rule.configParams,
            ordinal: rule.ordinal,
            documentControllers: [],
        });
    }

    completeRegistration() {
        this.currentScript = null;
        this.hasCompletedRegistration = true;
    }

    register(options: UIDocumentController.RegisterControllerFunctionOptions): void {
        if (this.hasCompletedRegistration) {
            throw new Error('Tried to register a controller function after registration was completed.');
        }

        if (!this.currentScript) {
            throw new Error('Tried to register a controller function before setting a Script.');
        }

        const scriptConfig = this.registeredRules.get(this.currentScript)!;

        this.registeredRules.set(this.currentScript, {
            ...scriptConfig,
            documentControllers: [...scriptConfig.documentControllers, options.controllerFn],
        });
    }

    async executeControllerFunctions({
        document,
        documentStateUpdater,
        contextInformationCollector,
        setFieldControlStates,
        poDocumentsForInvoice,
        setWorkflowApprovalConfiguration,
    }: {
        document: IDocumentViewModel;
        documentStateUpdater: (initialState: IDocumentViewModel, updatedState: IDocumentViewModel) => void;
        contextInformationCollector: ContextInformationCollector;
        setFieldControlStates: (states: IFieldControlStates) => void;
        poDocumentsForInvoice: PODocumentsForInvoice;
        setWorkflowApprovalConfiguration: UIDocumentController.ApprovalConfigurationUpdater.SetWorkflowApprovalConfiguration | null;
    }): Promise<void> {
        if (!this.hasCompletedRegistration) {
            throw new Error('Tried to execute controller functions before registration was completed.');
        }

        const controllerRegistrations = Array.from(this.registeredRules.values());

        // early return  if there are no controller functions
        if (controllerRegistrations.every(({ documentControllers }) => documentControllers.length === 0)) {
            return;
        }

        const initialDocumentState = JSON.parse(JSON.stringify(document)) as IDocumentViewModel;
        const mutableDocumentState = JSON.parse(JSON.stringify(document)) as IDocumentViewModel;

        if (this.registeredRules.size === 0) {
            this.writeToLog?.('No js scripts attached to mapping');
        }

        const documentFieldController = new DocumentFieldController();

        const rulesGroupedByOrdinal = controllerRegistrations.reduce((rulesRecord, rule) => {
            rulesRecord[rule.ordinal] ??= [];

            rulesRecord[rule.ordinal].push(rule);

            return rulesRecord;
        }, {} as Record<number, RuleRegistration[]>);

        const ruleExecutionGroups = Object.entries(rulesGroupedByOrdinal).map(([ordinal, rule]) => {
            const groupExecution = async () => {
                const ruleExecutionPromises = rule.map(
                    async ({ scriptName, configParams, documentControllers, dpsRuleName }) => {
                        this.writeToLog?.(`Executing controller functions for script: ${scriptName}`);

                        const controllerPromises = documentControllers.map(async (controllerFn) => {
                            await controllerFn({
                                context: this.context,
                                api: this.api,
                                document: mutableDocumentState,
                                configParams,
                                buyerCompanyId: this.buyerCompanyId,
                                senderCompanyId: this.senderCompanyId,
                                contextInformation: contextInformationCollector.getContextInformation(),
                                parentCompanyId: this.parentCompanyId,
                                documentFieldController,
                                dpsRuleName,
                                poDocuments: poDocumentsForInvoice,
                                setWorkflowApprovalConfiguration: setWorkflowApprovalConfiguration ?? (() => {}),
                            });
                        });
                        await Promise.all(controllerPromises);

                        this.writeToLog?.(`Finished executing controller functions for script: ${scriptName}`);
                    }
                );

                await Promise.all(ruleExecutionPromises);
            };

            return [ordinal, groupExecution] as const;
        });

        ruleExecutionGroups.sort((a, b) => Number(a[0]) - Number(b[0]));

        for await (const [ordinal, groupExecution] of ruleExecutionGroups) {
            this.writeToLog?.(`Executing controller functions for ordinal: ${ordinal}`);

            await groupExecution();

            this.writeToLog?.(`Finished executing controller functions for ordinal: ${ordinal}`);
        }

        documentStateUpdater(initialDocumentState, mutableDocumentState);

        setFieldControlStates(documentFieldController.getFieldControlStates());
    }
}
